/**
 * useTourQueue hook
 *
 * Manages tour queuing: queue both tours, queue a single tour, and
 * check/trigger queued tours on page visits. Returns raw TourData
 * so the CALLER can start the tour using its own useTour instance.
 */

import { useCallback } from "react"
import {
  getTourStatus,
  queueTour,
  startTour as startTourApi,
  type TourData,
} from "../services/tour.service"

export function useTourQueue() {
  /**
   * Queue both tours with their respective trigger conditions.
   * Fire-and-forget — errors are logged but don't block navigation.
   */
  const queueBothTours = useCallback(async () => {
    await Promise.allSettled([
      queueTour("home_tour", "on_home_visit"),
      queueTour("ritual_tour", "on_first_ritual_create"),
    ])
  }, [])

  /**
   * Queue only the home tour (used by path A profile modal).
   */
  const queueHomeTour = useCallback(async () => {
    await queueTour("home_tour", "on_home_visit").catch(() => {})
  }, [])

  /**
   * Check if a queued tour exists for the given type. If so, transition it
   * to started via the startTour API (which handles queued→started transition)
   * and return the TourData. Accepts optional metadata to store on the tour
   * (e.g. ritualId for ritual tours). Returns null if no queued tour exists.
   */
  const checkAndTriggerQueuedTour = useCallback(
    async (
      tourType: "home_tour" | "ritual_tour",
      metadata?: Record<string, unknown>,
    ): Promise<TourData | null> => {
      try {
        const status = await getTourStatus(tourType)
        const queued = status.queued
        if (!queued) return null

        const merged = { ...((queued.metadata as Record<string, unknown>) ?? {}), ...metadata }
        return await startTourApi(tourType, merged)
      } catch {
        return null
      }
    },
    [],
  )

  /**
   * Ensure a home tour exists for this user. startTour is idempotent on the
   * backend — returns existing active tour if one already exists, so no
   * separate existence check is needed.
   * Returns TourData if a new tour was created, null if already exists.
   */
  const ensureHomeTour = useCallback(async (): Promise<TourData | null> => {
    try {
      const status = await getTourStatus("home_tour")
      if (status.exists) return null
      return await startTourApi("home_tour")
    } catch {
      return null
    }
  }, [])

  /**
   * Ensure a ritual tour exists for this user.
   * Returns TourData if a new tour was created, null if already exists.
   */
  /**
   * Ensure a ritual tour exists for auto-start on page visits.
   * Only starts if the user has never completed or skipped a ritual tour.
   * (The creation flow bypasses this guard via redirectAfterRitualCreation.)
   */
  const ensureRitualTour = useCallback(
    async (metadata?: Record<string, unknown>): Promise<TourData | null> => {
      try {
        const status = await getTourStatus("ritual_tour")
        // Don't auto-restart for users who've already completed/skipped
        if (status.exists) return null
        return await startTourApi("ritual_tour", metadata)
      } catch {
        return null
      }
    },
    [],
  )

  return {
    queueBothTours,
    queueHomeTour,
    checkAndTriggerQueuedTour,
    ensureHomeTour,
    ensureRitualTour,
  }
}
